<template>
  <div class="select-area-cascader">
    <!-- 显示选中的区域名称 -->
    <a-input v-model="displayText" :placeholder="$t('请选择区域')" readonly @click="showModal" style="cursor: pointer">
      <a-icon v-if="selectedArea" slot="suffix" type="close-circle" @click.stop="clearSelection" />
    </a-input>

    <!-- 区域选择弹窗 -->
    <a-modal v-model="visible" :title="$t('请选择区域')" centered :width="800" :maskClosable="false" @cancel="handleCancel">
      <template slot="footer">
        <a-button @click="handleCancel">{{ $t('取消') }}</a-button>
        <a-button type="primary" @click="handleConfirm">{{ $t('确定') }}</a-button>
      </template>

      <a-input-search
        v-model="searchAreaValue"
        style="width: 100%; margin-top: 10px"
        allowClear
        :placeholder="$t('请输入区域名称')"
        @search="handleSearch"
      />

      <a-spin tip="Loading..." :spinning="areaLoading">
        <el-tree
          class="filter-tree"
          :data="areaTree"
          :props="defaultProps"
          :expand-on-click-node="false"
          :filter-node-method="filterNode"
          :highlight-current="true"
          node-key="id"
          :current-node-key="currentNodeKey"
          :default-expanded-keys="expandedKeys"
          @node-click="handleNodeClick"
          ref="areaTree"
        >
          <span
            slot-scope="{ node, data }"
            :class="{ 'custom-tree-node': true, 'is-selected': currentNodeKey === data.id }"
          >
            {{ node.label }}
          </span>
        </el-tree>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import { queryAreaTreeList } from '@/api/api'

export default {
  name: 'SelectArea',
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    // 父组件传入的区域路径，用于初始化回显
    areaPath: {
      type: String,
      default: '',
    },
    level: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      visible: false,
      searchAreaValue: '',
      areaTree: [],
      currentNodeKey: '',
      selectedArea: null,
      selectedPath: [],
      areaLoading: false,
      expandedKeys: [],
      defaultProps: {
        children: 'children',
        label: 'areaName',
      },
      treeLoaded: false,
      // 存储初始区域路径
      initialPath: '',
    }
  },
  computed: {
    displayText() {
      // 优先使用组件内部选择后的路径
      if (this.selectedPath.length) {
        return this.selectedPath.map(item => item.areaName).join('/')
      }

      // 如果没有内部路径但父组件传入了路径，使用父组件的路径
      if (this.initialPath) {
        return this.initialPath
      }

      return ''
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && this.treeLoaded) {
          this.$nextTick(() => {
            this.findAndSelectNode(newVal)
          })
        }
      },
    },
    // 监听父组件传入的areaPath变化
    areaPath: {
      immediate: true,
      handler(newPath) {
        this.initialPath = newPath
      },
    },
  },
  created() {
    // 保存传入的区域路径用于初始化显示
    if (this.areaPath) {
      this.initialPath = this.areaPath
    }
  },
  mounted() {
    // 如果已有区域ID，尝试初始化选择
    if (this.value) {
      // 如果树已经加载，直接查找
      if (this.treeLoaded) {
        this.findAndSelectNode(this.value)
      }
    }
  },
  methods: {
    showModal() {
      this.visible = true
      if (!this.treeLoaded) {
        this.loadTree()
      } else if (this.value) {
        this.$nextTick(() => {
          this.findAndSelectNode(this.value)
        })
      }
    },

    loadTree() {
      this.areaLoading = true
      queryAreaTreeList()
        .then(res => {
          if (res.success) {
            this.areaTree = this.level ? this.filterTreeByLevel(res.result, this.level) : res.result
            this.treeLoaded = true

            // 加载完成后尝试根据ID选择节点
            if (this.value) {
              this.findAndSelectNode(this.value)
            }
          }
        })
        .finally(() => (this.areaLoading = false))
    },

    // 按层级过滤树数据
    filterTreeByLevel(nodes, maxLevel, currentLevel = 1) {
      return nodes.map(node => {
        const newNode = { ...node }
        if (currentLevel < maxLevel && newNode.children) {
          newNode.children = this.filterTreeByLevel(newNode.children, maxLevel, currentLevel + 1)
        } else {
          delete newNode.children
        }
        return newNode
      })
    },

    // 根据区域ID查找并选择节点
    findAndSelectNode(areaId) {
      let found = false

      // 递归查找节点函数
      const findNode = (nodes, path = []) => {
        for (const node of nodes) {
          const currentPath = [...path, node]

          if (node.id == areaId) {
            this.currentNodeKey = node.id
            this.selectedArea = node
            this.selectedPath = currentPath

            // 设置树节点选中状态
            this.$nextTick(() => {
              if (this.$refs.areaTree) {
                this.$refs.areaTree.setCurrentKey(node.id)
                // 设置展开路径（排除当前节点）
                this.expandedKeys = currentPath.slice(0, -1).map(item => item.id)
              }
            })

            found = true
            return true
          }

          // 如果此节点有子节点，继续递归
          if (node.children && node.children.length > 0) {
            if (findNode(node.children, currentPath)) {
              return true
            }
          }
        }
        return false
      }

      // 开始查找
      findNode(this.areaTree)

      // 如果未找到但存在初始化路径，保留显示
      if (!found) {
        if (!this.initialPath && this.areaPath) {
          this.initialPath = this.areaPath
        }
      }
    },

    // 获取当前节点的完整路径
    getNodePath(node) {
      const path = []
      let currentNode = node

      while (currentNode && currentNode.data) {
        path.unshift(currentNode.data)
        currentNode = currentNode.parent
      }

      return path
    },

    // 节点点击处理
    handleNodeClick(data, node) {
      this.currentNodeKey = data.id
      this.selectedArea = data
      this.selectedPath = this.getNodePath(node)
    },

    // 确认选择
    handleConfirm() {
      if (!this.selectedArea) {
        this.$message.warning(this.$t('请先选择有效区域'))
        return
      }

      const pathText = this.selectedPath.map(item => item.areaName).join('/')

      // 触发事件
      this.$emit('input', this.selectedArea.id)
      this.$emit('change', {
        id: this.selectedArea.id,
        path: pathText,
        pathData: this.selectedPath,
      })

      this.visible = false
    },

    handleCancel() {
      this.visible = false

      // 恢复之前的选择
      if (this.value) {
        this.findAndSelectNode(this.value)
      } else {
        this.selectedArea = null
        this.selectedPath = []
      }
    },

    clearSelection() {
      this.selectedArea = null
      this.selectedPath = []
      this.initialPath = ''
      this.$emit('input', '')
      this.$emit('change', null)
      this.currentNodeKey = ''
    },

    handleSearch() {
      this.$refs.areaTree.filter(this.searchAreaValue)
    },

    filterNode(value, data) {
      if (!value) return true
      return data.areaName.includes(value)
    },
  },
}
</script>

<style scoped>
.select-area-cascader {
  width: 100%;
}

.ant-input-suffix {
  cursor: pointer;
}

/* 选中节点样式 */
.custom-tree-node.is-selected {
  color: #1890ff;
  font-weight: 500;
}
</style>
